import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Briefcase,
  FileText,
  Eye,
  Users,
  Megaphone,
  Sparkles,
} from "lucide-react";
import { PageShell } from "./publicShared";

/* ═══════════════════════════════════════════════════
   COMMUNITY & ECONOMIC DEVELOPMENT — INDUSTRY PAGE (v2)
   Revised after live CED lead feedback (Feb 2026).
   Dominant outcomes:
     • Grant & RFP Command Center
     • Impact Reporting & Funder Proof
     • Executive Visibility Dashboard
   Mobility is conditional.
   ═══════════════════════════════════════════════════ */

const VERTICAL_LABEL = "Community & Economic Development";
const VERTICAL_OFFERING = "Community & Economic Development Impact, Funding & AI Opportunity Assessment";
const PRELIM_TEMPLATE_ID = "community_economic_development_initial";

/* ─── HERO ─── */
const Hero = ({ navigate }) => {
  const goPrelim = () => {
    navigate(`/contact?intent=preliminary&template=${PRELIM_TEMPLATE_ID}&industry=community_economic_development`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 80);
  };
  const goCall = () => {
    navigate("/contact?intent=book&industry=community_economic_development");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 80);
  };

  return (
    <section className="bg-white py-16 lg:py-20" data-testid="ced-hero">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-start">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eff6ff] text-[#1a56db] text-[12px] font-semibold tracking-wide border border-[#bfdbfe]">
              <Sparkles className="h-3.5 w-3.5" /> {VERTICAL_LABEL}
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] leading-[1.05] tracking-tight" data-testid="ced-hero-headline">
              Use AI to Strengthen Funding, Prove Impact, and Improve Executive Visibility
            </h1>
            <p className="mt-5 text-lg text-[#4b5563] leading-relaxed max-w-2xl" data-testid="ced-hero-subheadline">
              Discover where AI can help your organization pursue more grants and RFPs, prove
              measurable outcomes, support fundraising, and give leadership a clearer view of
              what matters most.
            </p>
            <p className="mt-3 text-sm text-[#6b7280]">
              Built for organizations managing multiple programs, partners, deadlines, and funding stakeholders.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={goPrelim}
                size="lg"
                className="bg-[#1a56db] hover:bg-[#1e40af] text-white px-6"
                data-testid="ced-hero-primary-cta"
              >
                Start Preliminary Assessment <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button
                onClick={goCall}
                size="lg"
                variant="outline"
                className="border-[#e5e7eb] text-[#111827] px-6"
                data-testid="ced-hero-secondary-cta"
              >
                Schedule a Strategy Call
              </Button>
            </div>
          </div>

          {/* Hero card — Funding & Visibility Snapshot mock */}
          <div className="rounded-xl border border-[#e5e7eb] bg-[#f9fafb] p-6">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#6b7280] font-semibold mb-3">
              Funding &amp; Visibility Snapshot — Sample
            </div>
            <div className="space-y-3">
              {[
                { label: "Grant & RFP Command Center",       band: "Critical opportunity", color: "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]" },
                { label: "Impact Reporting & Funder Proof",  band: "Critical opportunity", color: "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]" },
                { label: "Executive Visibility Dashboard",   band: "High-value opportunity", color: "text-[#b45309] bg-[#fffbeb] border-[#fde68a]" },
                { label: "Workforce / Resident Intake",      band: "Moderate opportunity", color: "text-[#a16207] bg-[#fefce8] border-[#fde047]" },
                { label: "Community Signal Analysis",        band: "Moderate opportunity", color: "text-[#a16207] bg-[#fefce8] border-[#fde047]" },
              ].map((r, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg bg-white border border-[#e5e7eb] px-3 py-2.5">
                  <span className="text-sm text-[#111827] font-medium">{r.label}</span>
                  <span className={`text-[11px] font-semibold px-2 py-1 rounded border ${r.color}`}>{r.band}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[12px] text-[#6b7280]">
              Sample output of the Preliminary Assessment. Real results are tied to your organization's answers.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── WHY THIS MATTERS NOW ─── */
const PAIN_POINTS = [
  "Grants and RFPs depend too heavily on individual effort.",
  "Impact reporting is too manual and fragmented.",
  "Leadership lacks one current view across deadlines, owners, and risks.",
  "Operational knowledge is trapped in files and people's heads.",
  "Community input is hard to organize and act on.",
  "Funding conversations require more proof than ever.",
];

const WhyThisMatters = () => (
  <section className="bg-[#f9fafb] py-16 lg:py-20" data-testid="ced-why-now">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Why this matters now</span>
        <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
          The gap between what your team produces and what funders expect is widening.
        </h2>
        <p className="mt-4 text-lg text-[#4b5563] leading-relaxed">
          Most community and economic development organizations are running too lean to keep up
          with the level of proof, visibility, and responsiveness that today's funding environment
          demands. These are the patterns we see most often.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PAIN_POINTS.map((pain, i) => (
          <div key={i} className="rounded-xl border border-[#e5e7eb] bg-white p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold mb-2">Pattern {i + 1}</div>
            <p className="text-[15px] text-[#111827] leading-relaxed font-medium">{pain}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── TOP WORKFLOWS ─── */
const WORKFLOWS = [
  {
    icon: Briefcase,
    title: "Grant & RFP Command Center",
    explanation:
      "A single command center for finding, prioritizing, drafting, and tracking grants, RFPs, renewals, and contract opportunities — so the team stops reinventing the wheel on every submission.",
    why: "Strengthens funding pursuit, reduces submission drag, and gives leadership a real view of what is due, who owns it, and what is at risk.",
  },
  {
    icon: FileText,
    title: "Impact Reporting & Funder Proof",
    explanation:
      "Outcome evidence assembled cleanly across programs so board, donor, and funder reports can be produced without major manual cleanup.",
    why: "Strengthens renewals, fundraising, and the ability to prove what changed and what was achieved.",
  },
  {
    icon: Eye,
    title: "Executive Visibility Dashboard",
    explanation:
      "A live executive view of active grants, reporting deadlines, owners, program status, and red flags — so leadership can see what is on track and what is at risk.",
    why: "Strengthens oversight, accountability, and early intervention on risks before they become urgent.",
  },
  {
    icon: Users,
    title: "Workforce / Resident Intake, Matching & Follow-Up Automation",
    explanation:
      "Intake, matching, and follow-up automation — to reduce administrative drag, improve case flow, and help staff spend more time on direct service.",
    why: "Strengthens operational throughput and the evidence trail underlying workforce / resident outcomes.",
  },
  {
    icon: Megaphone,
    title: "Community Signal Analysis & Intervention Prioritization",
    explanation:
      "Community input from meetings, surveys, outreach, and partners turned into structured, searchable signal — so leadership can spot recurring needs and geographic patterns.",
    why: "Strengthens the link between community input and the interventions you choose to prioritize.",
  },
];

const TopWorkflows = ({ navigate }) => (
  <section className="bg-white py-16 lg:py-20" data-testid="ced-top-workflows">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Top AI Workflows</span>
        <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
          The five workflows that move the needle most for organizations like yours.
        </h2>
        <p className="mt-4 text-lg text-[#4b5563] leading-relaxed">
          Every recommendation we make is anchored to one of these workflow archetypes — the
          ones we see drive the strongest funding, proof, and visibility outcomes for community
          and economic development organizations.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {WORKFLOWS.map((wf, i) => {
          const Icon = wf.icon;
          return (
            <div key={i} className="rounded-xl border border-[#e5e7eb] bg-white p-6 hover:border-[#1a56db]/40 transition-colors" data-testid={`ced-workflow-card-${i + 1}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[#eff6ff] text-[#1a56db]">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Workflow {i + 1}</div>
              </div>
              <h3 className="text-[17px] font-bold text-[#111827] mb-2 leading-tight">{wf.title}</h3>
              <p className="text-sm text-[#4b5563] leading-relaxed mb-3">{wf.explanation}</p>
              <p className="text-sm text-[#1a56db] leading-relaxed font-medium">
                Why it matters: <span className="text-[#374151] font-normal">{wf.why}</span>
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-6 rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-4 text-sm text-[#4b5563]" data-testid="ced-mobility-conditional-note">
        <strong className="text-[#111827]">Mobility / Service Delivery Optimization</strong> is also
        relevant for organizations that operate transportation or field-based service delivery. The
        assessment surfaces it conditionally when that scope applies.
      </div>
    </div>
  </section>
);

/* ─── ASSESSMENT MODEL ─── */
const AssessmentModel = ({ navigate }) => {
  const goPrelim = () => {
    navigate(`/contact?intent=preliminary&template=${PRELIM_TEMPLATE_ID}&industry=community_economic_development`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 80);
  };
  return (
    <section className="bg-[#f9fafb] py-16 lg:py-20" data-testid="ced-assessment-model">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Two-Stage Assessment Model</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
            A fast diagnostic, then a deeper validation.
          </h2>
        </div>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-7">
            <div className="text-[11px] uppercase tracking-wider text-[#1a56db] font-semibold">Stage 1</div>
            <h3 className="mt-2 text-2xl font-bold text-[#111827]">Preliminary Assessment</h3>
            <p className="mt-3 text-[15px] text-[#4b5563] leading-relaxed">
              A fast diagnostic to identify where AI can most improve funding pursuit, impact
              proof, executive visibility, and operational efficiency.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#374151]">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> 15–20 minute commitment, 21 questions total</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> 6 scoping questions + 15 scored capability questions</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> Top 3 opportunity areas surfaced immediately</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> Funding Readiness + Executive Visibility snapshots</li>
            </ul>
            <Button onClick={goPrelim} className="mt-6 bg-[#1a56db] hover:bg-[#1e40af] text-white" data-testid="ced-prelim-cta">
              Begin Preliminary Assessment <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <div className="rounded-xl border border-[#e5e7eb] bg-white p-7">
            <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Stage 2</div>
            <h3 className="mt-2 text-2xl font-bold text-[#111827]">In-depth Assessment</h3>
            <p className="mt-3 text-[15px] text-[#4b5563] leading-relaxed">
              A deeper operational assessment that validates the highest-value workflows, ranks
              priorities, and recommends the right 60–90 day pilot path.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-[#374151]">
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> 60–90 minutes, sectioned across 7 operational areas</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> Quantifies operational and funding pain</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> AI-enriched workflow ranking with KPIs and dependencies</li>
              <li className="flex items-start gap-2"><Check className="h-4 w-4 text-[#1a56db] mt-0.5 shrink-0" /> Recommended 60–90 day pilot path</li>
            </ul>
            <div className="mt-6 text-sm text-[#6b7280] italic">
              The In-depth Assessment unlocks after the Preliminary Assessment is reviewed with our team.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── EXPECTED OUTCOMES ─── */
const OUTCOMES = [
  "Stronger grant and RFP execution",
  "Clearer impact reporting and funder proof",
  "Better executive visibility",
  "Faster proposal and reporting readiness",
  "Reduced administrative drag",
  "Stronger fundraising support",
  "Better accountability across owners and deadlines",
];

const ExpectedOutcomes = () => (
  <section className="bg-white py-16 lg:py-20" data-testid="ced-expected-outcomes">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Expected Outcomes</span>
        <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
          What organizations typically see within 60–90 days.
        </h2>
      </div>
      <div className="mt-8 flex flex-wrap gap-3">
        {OUTCOMES.map((o, i) => (
          <div key={i} className="inline-flex items-center gap-2 rounded-full border border-[#e5e7eb] bg-[#f9fafb] px-4 py-2">
            <Check className="h-4 w-4 text-[#1a56db]" />
            <span className="text-sm text-[#111827] font-medium">{o}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── WHO THIS IS FOR ─── */
const PERSONAS = [
  "Community development organizations",
  "Economic development partnerships",
  "Workforce intermediaries",
  "Place-based nonprofit initiatives",
  "Neighborhood revitalization organizations",
  "Community collaboratives",
  "Nonprofit and civic operators with multiple funding streams",
];

const WhoThisIsFor = () => (
  <section className="bg-[#f9fafb] py-16 lg:py-20" data-testid="ced-who-this-is-for">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
      <div className="max-w-3xl">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Who this is for</span>
        <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
          Organizations that have to prove impact, pursue funding, and run programs at the same time.
        </h2>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PERSONAS.map((p, i) => (
          <div key={i} className="rounded-lg border border-[#e5e7eb] bg-white p-4">
            <div className="text-[15px] text-[#111827] font-medium">{p}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── FAQ ─── */
const FAQS = [
  {
    q: "Is this only for large organizations?",
    a: "No. This assessment is designed for small to mid-sized community and economic development organizations as well — anywhere the team is running too lean to keep up with funding, reporting, and executive visibility demands.",
  },
  {
    q: "Do we need to already be using AI?",
    a: "No prior AI experience is required. The assessment identifies where AI can help, the readiness conditions you'd need to put in place, and how to sequence pilots that don't disrupt the rest of operations.",
  },
  {
    q: "Will AI replace staff judgment?",
    a: "No. Every recommended CED workflow keeps a human in the loop where it matters — eligibility decisions, funder narratives, intervention prioritization, equity tradeoffs, and final proposal sign-off.",
  },
  {
    q: "Can this help with grants and reporting?",
    a: "Yes — those are the two dominant outcomes of this vertical. The Grant & RFP Command Center and the Impact Reporting & Funder Proof workflows are designed specifically to strengthen funding pursuit, renewals, and outcome evidence.",
  },
  {
    q: "What do we receive after the In-depth Assessment?",
    a: "A ranked top-5 workflow plan tied to your actual answers, a 60–90 day pilot path, KPI baselines and targets per workflow, governance and sequencing notes, and a clear human-in-loop boundary for each recommendation.",
  },
];

const FAQItem = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid={`ced-faq-item-${idx + 1}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-[15px] font-semibold text-[#111827]">{q}</span>
        {open ? <ChevronUp className="h-5 w-5 text-[#6b7280] shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#6b7280] shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm text-[#4b5563] leading-relaxed">{a}</p>
        </div>
      )}
    </div>
  );
};

const FAQ = () => (
  <section className="bg-white py-16 lg:py-20" data-testid="ced-faq">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
      <div className="text-center">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#1a56db] font-semibold">Frequently Asked</span>
        <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-[#111827] leading-tight">
          Common questions from CED leaders.
        </h2>
      </div>
      <div className="mt-10 space-y-3">
        {FAQS.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} />)}
      </div>
    </div>
  </section>
);

/* ─── FINAL CTA ─── */
const FinalCTA = ({ navigate }) => {
  const goPrelim = () => {
    navigate(`/contact?intent=preliminary&template=${PRELIM_TEMPLATE_ID}&industry=community_economic_development`);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 80);
  };
  const goCall = () => {
    navigate("/contact?intent=book&industry=community_economic_development");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" }), 80);
  };
  return (
    <section className="bg-[#0b1220] py-20" data-testid="ced-final-cta">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 text-center">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" data-testid="ced-final-cta-headline">
          See where AI can improve funding, proof, and execution.
        </h2>
        <p className="mt-4 text-lg text-[#9ca3af] max-w-2xl mx-auto">
          {VERTICAL_OFFERING}.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={goPrelim}
            size="lg"
            className="bg-white hover:bg-[#f3f4f6] text-[#0b1220] px-6"
            data-testid="ced-final-primary-cta"
          >
            Start Preliminary Assessment <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <Button
            onClick={goCall}
            size="lg"
            variant="outline"
            className="border-white/30 text-white hover:bg-white/10 px-6"
            data-testid="ced-final-secondary-cta"
          >
            Book a Conversation
          </Button>
        </div>
      </div>
    </section>
  );
};

/* ─── PAGE ─── */
const CEDIndustryPage = () => {
  const navigate = useNavigate();
  return (
    <PageShell>
      <Hero navigate={navigate} />
      <WhyThisMatters />
      <TopWorkflows navigate={navigate} />
      <AssessmentModel navigate={navigate} />
      <ExpectedOutcomes />
      <WhoThisIsFor />
      <FAQ />
      <FinalCTA navigate={navigate} />
    </PageShell>
  );
};

export default CEDIndustryPage;
